import React, { useEffect, useState } from "react";
import api, { formatDateTime, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface } from "@/components/app/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, ShieldCheck, Copy, Trash2, CheckCircle2, XCircle, KeyRound, Link2, PlugZap } from "lucide-react";

/* Enterprise SSO — connect the customer's Azure AD / Okta / Keycloak / PingOne.
 * Identity proves WHO someone is; SAHOD still decides what they may see. */

const BLANK = {
    name: "", protocol: "oidc", domains: "", enabled: true, auto_provision: false,
    default_role: "employee", enforce_sso: false,
    discovery_url: "", client_id: "", client_secret: "", scopes: "openid email profile",
    sso_url: "", idp_entity_id: "", idp_certificate: "", email_attribute: "",
};

export default function SSOSettings() {
    const [items, setItems] = useState([]);
    const [events, setEvents] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(BLANK);
    const [editing, setEditing] = useState(null);
    const [urls, setUrls] = useState(null);   // {name, ...urls}
    const [testResult, setTestResult] = useState(null);

    const load = () => Promise.all([
        api.get("/sso/admin/connections").then((r) => setItems(r.data.items || [])),
        api.get("/sso/admin/events").then((r) => setEvents(r.data.items || [])),
    ]).catch(() => {});
    useEffect(() => { load(); }, []);

    const openNew = () => { setForm(BLANK); setEditing(null); setOpen(true); };
    const openEdit = (c) => {
        setForm({ ...BLANK, ...c, domains: (c.domains || []).join(", "),
                  client_secret: "", idp_certificate: "" });
        setEditing(c); setOpen(true);
    };

    const save = async () => {
        const payload = { ...form, domains: form.domains.split(",").map((d) => d.trim()).filter(Boolean) };
        // Blank secret on edit = "leave the stored one alone".
        if (editing && !payload.client_secret) delete payload.client_secret;
        if (editing && !payload.idp_certificate) delete payload.idp_certificate;
        try {
            const { data } = editing
                ? await api.put(`/sso/admin/connections/${editing.id}`, payload)
                : await api.post("/sso/admin/connections", payload);
            toast.success(editing ? "Connection updated" : "Connection created");
            setOpen(false);
            if (!editing) showUrls(data.id, data.name);
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Could not save"); }
    };

    const showUrls = async (id, name) => {
        try { const { data } = await api.get(`/sso/admin/connections/${id}/urls`); setUrls({ ...data, name }); }
        catch { toast.error("Could not load the connection URLs"); }
    };

    const test = async (c) => {
        setTestResult({ loading: true, name: c.name });
        try { const { data } = await api.post(`/sso/admin/connections/${c.id}/test`); setTestResult({ ...data, name: c.name }); }
        catch (e) { setTestResult({ ok: false, name: c.name, detail: formatErrorDetail(e.response?.data?.detail) || "Test failed", checks: [] }); }
    };

    const remove = async (c) => {
        if (!window.confirm(`Remove ${c.name}? Anyone relying on it will have to sign in with a password.`)) return;
        try { await api.delete(`/sso/admin/connections/${c.id}`); toast.success("Connection removed"); load(); }
        catch { toast.error("Could not remove it"); }
    };

    const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success("Copied"), () => {});
    const Row = ({ label, value }) => (
        <div>
            <Label className="text-xs">{label}</Label>
            <div className="flex items-center gap-2 mt-1">
                <Input readOnly value={value} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
                <button className="h-9 px-3 rounded-md bg-secondary text-sm shrink-0" onClick={() => copy(value)}><Copy className="size-4" /></button>
            </div>
        </div>
    );

    return (
        <div>
            <PageHeader
                title="Single Sign-On"
                subtitle="Let staff sign in with the company directory — Azure AD / Entra, Okta, Keycloak, PingOne or any OIDC / SAML 2.0 provider."
                actions={
                    <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2"
                            onClick={openNew} data-testid="sso-add">
                        <Plus className="size-4" /> Add connection
                    </button>
                } />

            {!items.length && (
                <Surface className="p-8 text-center" data-testid="sso-empty">
                    <ShieldCheck className="size-10 mx-auto text-muted-foreground/40 mb-3" />
                    <h3 className="font-semibold">No SSO connections yet</h3>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xl mx-auto">
                        Add a connection, hand the generated URLs to the customer's IT administrator, and staff on that
                        email domain get a “Continue with SSO” button. Password sign-in keeps working alongside it.
                    </p>
                </Surface>
            )}

            <div className="grid gap-3 md:grid-cols-2">
                {items.map((c) => (
                    <Surface key={c.id} className="p-4" data-testid={`sso-conn-${c.id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <ShieldCheck className="size-4 text-primary shrink-0" />
                                    <span className="font-semibold truncate">{c.name}</span>
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary uppercase font-bold">{c.protocol}</span>
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${c.enabled
                                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                        : "bg-muted text-muted-foreground border-border"}`}>
                                        {c.enabled ? "Enabled" : "Disabled"}
                                    </span>
                                </div>
                                <div className="text-xs text-muted-foreground mt-1.5 space-y-0.5">
                                    <div>Domains: {(c.domains || []).join(", ") || <span className="text-amber-700">none set — nobody will see the SSO button</span>}</div>
                                    <div>
                                        {c.auto_provision
                                            ? <>New users created automatically as <b>{c.default_role}</b></>
                                            : "Users must already exist in SAHOD"}
                                    </div>
                                    {c.enforce_sso && <div className="text-amber-700">Password sign-in blocked for these domains</div>}
                                </div>
                            </div>
                            <div className="flex flex-col gap-1 shrink-0">
                                <button className="text-xs px-2 py-1 rounded border border-border flex items-center gap-1" onClick={() => test(c)} data-testid={`sso-test-${c.id}`}>
                                    <PlugZap className="size-3" /> Test
                                </button>
                                <button className="text-xs px-2 py-1 rounded border border-border flex items-center gap-1" onClick={() => showUrls(c.id, c.name)}>
                                    <Link2 className="size-3" /> URLs
                                </button>
                                <button className="text-xs px-2 py-1 rounded border border-border" onClick={() => openEdit(c)}>Edit</button>
                                <button className="text-xs px-2 py-1 rounded border border-destructive/40 text-destructive flex items-center gap-1" onClick={() => remove(c)}>
                                    <Trash2 className="size-3" /> Remove
                                </button>
                            </div>
                        </div>
                    </Surface>
                ))}
            </div>

            {events.length > 0 && (
                <Surface className="p-0 mt-4 overflow-hidden">
                    <h3 className="text-sm font-semibold px-4 pt-4 pb-2">Recent sign-in activity</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40">
                                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                                    <th className="px-3 py-2">When</th><th className="px-3 py-2">Result</th>
                                    <th className="px-3 py-2">Who</th><th className="px-3 py-2">Detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.slice(0, 25).map((e) => (
                                    <tr key={e.id} className="border-t border-border">
                                        <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(e.at)}</td>
                                        <td className="px-3 py-2">
                                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                                                e.outcome === "success" ? "bg-emerald-50 text-emerald-700"
                                                    : e.outcome === "denied" ? "bg-amber-50 text-amber-800"
                                                    : "bg-red-50 text-red-700"}`}>{e.outcome}</span>
                                        </td>
                                        <td className="px-3 py-2 text-xs">{e.email || "—"}</td>
                                        <td className="px-3 py-2 text-xs text-muted-foreground">{e.detail}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Surface>
            )}

            {/* Add / edit */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{editing ? `Edit ${editing.name}` : "Add an SSO connection"}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="grid md:grid-cols-2 gap-3">
                            <div><Label>Name</Label><Input value={form.name} placeholder="Acme Azure AD"
                                                           onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sso-name" /></div>
                            <div>
                                <Label>Protocol</Label>
                                <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm"
                                        value={form.protocol} onChange={(e) => setForm({ ...form, protocol: e.target.value })} data-testid="sso-protocol">
                                    <option value="oidc">OpenID Connect (recommended)</option>
                                    <option value="saml">SAML 2.0</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <Label>Email domains</Label>
                            <Input value={form.domains} placeholder="acme.ph, acme.com"
                                   onChange={(e) => setForm({ ...form, domains: e.target.value })} data-testid="sso-domains" />
                            <p className="text-xs text-muted-foreground mt-1">Staff with these email domains get the “Continue with SSO” button.</p>
                        </div>

                        {form.protocol === "oidc" ? (
                            <div className="space-y-3 border-t pt-3">
                                <div>
                                    <Label>Discovery URL</Label>
                                    <Input value={form.discovery_url} placeholder="https://login.microsoftonline.com/<tenant>/v2.0/.well-known/openid-configuration"
                                           onChange={(e) => setForm({ ...form, discovery_url: e.target.value })} className="font-mono text-xs" data-testid="sso-discovery" />
                                </div>
                                <div className="grid md:grid-cols-2 gap-3">
                                    <div><Label>Client ID</Label><Input value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="font-mono text-xs" /></div>
                                    <div>
                                        <Label>Client secret {editing && <span className="text-xs text-muted-foreground">(leave blank to keep)</span>}</Label>
                                        <Input type="password" value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })} />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-3 border-t pt-3">
                                <div><Label>IdP sign-in URL</Label><Input value={form.sso_url} placeholder="https://login.example.com/app/sso/saml"
                                                                          onChange={(e) => setForm({ ...form, sso_url: e.target.value })} className="font-mono text-xs" /></div>
                                <div><Label>IdP entity ID</Label><Input value={form.idp_entity_id} onChange={(e) => setForm({ ...form, idp_entity_id: e.target.value })} className="font-mono text-xs" /></div>
                                <div>
                                    <Label>IdP signing certificate {editing && <span className="text-xs text-muted-foreground">(leave blank to keep)</span>}</Label>
                                    <textarea className="w-full h-24 rounded-md border border-border bg-background p-2 font-mono text-[11px]"
                                              placeholder="-----BEGIN CERTIFICATE----- …"
                                              value={form.idp_certificate} onChange={(e) => setForm({ ...form, idp_certificate: e.target.value })} />
                                    <p className="text-xs text-muted-foreground mt-1">Required — assertions without a signature we can verify are rejected.</p>
                                </div>
                                <div><Label>Email attribute (optional)</Label><Input value={form.email_attribute} placeholder="Leave blank to auto-detect"
                                                                                    onChange={(e) => setForm({ ...form, email_attribute: e.target.value })} className="font-mono text-xs" /></div>
                            </div>
                        )}

                        <div className="border-t pt-3 space-y-2">
                            <label className="flex items-start gap-2 text-sm">
                                <input type="checkbox" className="mt-1" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
                                <span>Enabled</span>
                            </label>
                            <label className="flex items-start gap-2 text-sm">
                                <input type="checkbox" className="mt-1" checked={form.auto_provision}
                                       onChange={(e) => setForm({ ...form, auto_provision: e.target.checked })} data-testid="sso-autoprov" />
                                <span>
                                    Create accounts automatically on first sign-in
                                    <span className="block text-xs text-muted-foreground">
                                        Off by default: being in the directory shouldn't by itself grant access to payroll data.
                                    </span>
                                </span>
                            </label>
                            {form.auto_provision && (
                                <div className="ml-6">
                                    <Label>Role for new accounts</Label>
                                    <select className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                                            value={form.default_role} onChange={(e) => setForm({ ...form, default_role: e.target.value })}>
                                        {["employee", "manager", "hr_staff"].map((r) => <option key={r} value={r}>{r}</option>)}
                                    </select>
                                </div>
                            )}
                            <label className="flex items-start gap-2 text-sm">
                                <input type="checkbox" className="mt-1" checked={form.enforce_sso} onChange={(e) => setForm({ ...form, enforce_sso: e.target.checked })} />
                                <span>
                                    Require SSO for these domains
                                    <span className="block text-xs text-muted-foreground">Blocks password sign-in. Administrators keep a password fallback so you can't be locked out.</span>
                                </span>
                            </label>
                        </div>
                    </div>
                    <DialogFooter>
                        <button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={save} data-testid="sso-save">
                            {editing ? "Save changes" : "Create connection"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* URLs for the IdP administrator */}
            <Dialog open={!!urls} onOpenChange={(v) => !v && setUrls(null)}>
                <DialogContent className="max-w-xl">
                    <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="size-4 text-primary" /> Settings for {urls?.name}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">Send these to whoever administers the identity provider.</p>
                        <Row label="Redirect URI / Reply URL (OIDC)" value={urls?.redirect_uri || ""} />
                        <Row label="Assertion Consumer Service URL (SAML)" value={urls?.acs_url || ""} />
                        <Row label="Service Provider entity ID" value={urls?.entity_id || ""} />
                        <Row label="SP metadata" value={urls?.metadata_url || ""} />
                        <Row label="Direct sign-in link" value={urls?.login_url || ""} />
                    </div>
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={() => setUrls(null)}>Done</button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Test result */}
            <Dialog open={!!testResult} onOpenChange={(v) => !v && setTestResult(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Connection test — {testResult?.name}</DialogTitle></DialogHeader>
                    {testResult?.loading ? <div className="py-6 text-center text-sm text-muted-foreground">Checking the identity provider…</div> : (
                        <div className="space-y-2">
                            <div className={`text-sm font-semibold ${testResult?.ok ? "text-emerald-700" : "text-red-700"}`}>
                                {testResult?.ok ? "Everything checks out" : "Not ready yet"}
                            </div>
                            {(testResult?.checks || []).map((c, i) => (
                                <div key={i} className="flex items-center gap-2 text-sm">
                                    {c.ok ? <CheckCircle2 className="size-4 text-emerald-600" /> : <XCircle className="size-4 text-red-600" />}
                                    {c.name}
                                </div>
                            ))}
                            {testResult?.issuer && <div className="text-xs text-muted-foreground font-mono mt-1">issuer: {testResult.issuer}</div>}
                            {testResult?.detail && <p className="text-sm text-muted-foreground pt-1">{testResult.detail}</p>}
                        </div>
                    )}
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-secondary text-sm" onClick={() => setTestResult(null)}>Close</button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
